import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useMutationAddDatabase, useQueryCompanyDatabases, isPendingDatabaseStatus } from '@/lib/api/company/useCompanyDatabaseHooks'

interface AddDatabaseModalProps {
  isOpen: boolean
  close: () => void
  companyId: string
}

const AddDatabaseModal: React.FC<AddDatabaseModalProps> = ({
  isOpen,
  close,
  companyId,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const addDatabaseMutation = useMutationAddDatabase(companyId)
  const { data: databases } = useQueryCompanyDatabases(companyId)
  const [databaseName, setDatabaseName] = React.useState('')
  const [label, setLabel] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isCreating, setIsCreating] = React.useState(false)

  React.useEffect(() => {
    if (isOpen) {
      setDatabaseName('')
      setLabel('')
      setError(null)
      setIsCreating(false)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (isCreating && databases && !databases.some((db) => isPendingDatabaseStatus(db.status))) {
      setIsCreating(false)
      close()
    }
  }, [databases, isCreating, close])

  const activeServerId = databases?.find((db) => db.isActive)?.serverId

  const handleConfirm = async () => {
    setError(null)

    const stopLoading = startLoading()
    try {
      await addDatabaseMutation.mutateAsync({
        companyId,
        serverId: activeServerId!,
        databaseName: databaseName || undefined,
        label: label || undefined,
      })
      setIsCreating(true)
    } catch (err: any) {
      const code = err?.response?.data?.code
      if (code === 'DATABASE_EXISTS') {
        setError(t('databases.database-name-exists'))
      } else {
        setError(
          err?.response?.data?.message ||
          err?.message ||
          t('databases.add-database-error'),
        )
      }
    } finally {
      stopLoading()
    }
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[500px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        {/* Header */}
        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <p className="tw-text-xl tw-font-semibold tw-text-content-base">
              {isCreating ? t('databases.db-initialization') : t('administrationPanel-companies.database-add-new')}
            </p>
            {!isCreating && (
              <FaIcon
                icon={faClose}
                className="tw-h-5 tw-w-5 tw-cursor-pointer"
                onClick={close}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          {isCreating ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-py-8">
              <LoaderIcon className="tw-animate-spin tw-h-10 tw-w-10 tw-text-primary" />
              <p className="tw-text-base tw-text-content-base">
                {t('databases.db-initializing')}
              </p>
            </div>
          ) : (
            <div className="tw-space-y-4">
              <div>
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                  {t('administrationPanel-companies.database-name')}
                </label>
                <input
                  type="text"
                  value={databaseName}
                  onChange={(e) => setDatabaseName(e.target.value)}
                  placeholder={t('administrationPanel-companies.database-name-placeholder')}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base placeholder:tw-text-gray-400 focus:tw-border-primary focus:tw-outline-none"
                />
              </div>

              <div>
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                  {t('administrationPanel-companies.database-label')}
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('administrationPanel-companies.database-label-placeholder')}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base placeholder:tw-text-gray-400 focus:tw-border-primary focus:tw-outline-none"
                />
              </div>

              {error && (
                <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-4">
                  <p className="tw-text-sm tw-text-red-800">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isCreating && (
          <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
            <div className="tw-flex tw-justify-end tw-gap-3">
              <button
                type="button"
                onClick={close}
                disabled={loading}
                className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
              >
                {t('administrationPanel-companies.duplicate-database-cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading || !activeServerId}
                className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
              >
                {t('administrationPanel-companies.database-add-confirm')}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalLayout>
  )
}

export default AddDatabaseModal
